import { api } from "@/api/utils";
import { ChartBox, formatAxisNumber, useContainerSize } from "@/components/charts/ChartBox";
import { toDateOnly } from "@common/utils/date";
import { formatDollars, formatWholeNumber } from "@common/utils/i18n";
import { Box, BoxProps, Grid, Text } from "@givenwell/components";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { defineChart, rect } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { scaleUtc } from "d3-scale";
import { ReactNode, Suspense, useMemo } from "react";

export const Route = createFileRoute("/dashboard/costs")({
  component: Page,
});

function Page() {
  return (
    <Grid
      css={{
        width: "100%",
        height: "100%",

        gap: 12,
        gridCols: 3,
        gridTemplateRows: "repeat(2, 1fr)",
      }}
    >
      <Suspense fallback={<Card />}>
        <Airwallex />
      </Suspense>
      <Card css={{ flex: "1 0 0px" }} />
      <Card css={{ flex: "1 0 0px" }} />
      <Card css={{ flex: "1 0 0px" }} />
      <Card css={{ flex: "1 0 0px" }} />
      <Card css={{ flex: "1 0 0px" }} />
    </Grid>
  );
}

const getMonthBounds = () => {
  const now = new Date();
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  const startOfReportingPeriod = new Date(startOfThisMonth);
  startOfReportingPeriod.setMonth(startOfReportingPeriod.getMonth() - 6);
  return {
    startOfLastMonth,
    startOfThisMonth,
    startOfReportingPeriod,
  };
};

type PayoutMonthRow = {
  start: Date;
  end: Date;
  payment_type: "claim" | "supplier";
  y1: number;
  y2: number;
};

function Airwallex() {
  const { startOfLastMonth, startOfThisMonth, startOfReportingPeriod } = getMonthBounds();
  const from = toDateOnly(startOfReportingPeriod);
  const to = toDateOnly(new Date());
  const { data: revenue } = useSuspenseQuery({
    queryKey: ["reports", "costs", "airwallex-payouts", { from, to }],
    queryFn: async () => {
      return await api.reporting.getManagementReportingObsAirwallexPayouts({
        from,
        to,
      });
    },
  });

  const lastMonthAirwallexPayouts = revenue.filter(
    r => new Date(r.paid_at) >= startOfLastMonth && new Date(r.paid_at) < startOfThisMonth,
  );
  const thisMonthAirwallexPayouts = revenue.filter(r => new Date(r.paid_at) >= startOfThisMonth);

  const lastMonthAirwallexCount = lastMonthAirwallexPayouts.length;
  const thisMonthAirwallexCount = thisMonthAirwallexPayouts.length;

  const lastMonthWasBigger = lastMonthAirwallexCount > thisMonthAirwallexCount;

  const biggerAirwallexCount = lastMonthWasBigger ? lastMonthAirwallexCount : thisMonthAirwallexCount;

  const { total: fees, nextBracket } = getAirwallexFeesUSD(biggerAirwallexCount);

  // Count payouts per UTC month (matching Plot's UTC month binning), stacked
  // by payment type: supplier payouts on the bottom, claim payouts on top.
  const rows = useMemo(() => {
    const months = new Map<number, { start: Date; end: Date; claim: number; supplier: number }>();
    for (const payout of revenue) {
      const paidAt = new Date(payout.paid_at);
      const startTime = Date.UTC(paidAt.getUTCFullYear(), paidAt.getUTCMonth(), 1);
      let month = months.get(startTime);
      if (!month) {
        month = {
          start: new Date(startTime),
          end: new Date(Date.UTC(paidAt.getUTCFullYear(), paidAt.getUTCMonth() + 1, 1)),
          claim: 0,
          supplier: 0,
        };
        months.set(startTime, month);
      }
      if (payout.payment_type === "claim") {
        month.claim++;
      } else {
        month.supplier++;
      }
    }
    const stacked: PayoutMonthRow[] = [];
    for (const month of [...months.values()].sort((a, b) => a.start.getTime() - b.start.getTime())) {
      if (month.supplier > 0) {
        stacked.push({ start: month.start, end: month.end, payment_type: "supplier", y1: 0, y2: month.supplier });
      }
      if (month.claim > 0) {
        stacked.push({
          start: month.start,
          end: month.end,
          payment_type: "claim",
          y1: month.supplier,
          y2: month.supplier + month.claim,
        });
      }
    }
    return stacked;
  }, [revenue]);

  const definition = useMemo(
    () =>
      defineChart({
        marks: [
          rect(rows, {
            x1: "start",
            x2: "end",
            y1: "y1",
            y2: "y2",
            color: "payment_type",
          }),
        ],
        scales: {
          x: {
            scale: scaleUtc,
            axis: {
              line: false,
              ticks: { size: 0 },
              tickLabels: { fontSize: 12, opacity: 1 },
            },
          },
          y: {
            scale: scaleLinear,
            grid: false,
            axis: {
              line: false,
              ticks: { count: 10, size: 0, format: formatAxisNumber },
              tickLabels: { fontSize: 12, opacity: 1 },
              label: "No. of payouts",
            },
          },
        },
        color: {
          domain: ["supplier", "claim"],
          range: [colors.blue300, colors.red500],
        },
      }),
    [rows],
  );

  const { ref, height } = useContainerSize();

  return (
    <Card title="Airwallex Payout Fees" css={{ display: "flex", flexDirection: "column", flex: "1 0 0px" }}>
      <BigValue>
        {formatDollars(fees)}
        <Brackets> USD p. m</Brackets>
      </BigValue>
      <ValueDescription>
        {formatDollars(fees / biggerAirwallexCount)} per payout &bull;{" "}
        {nextBracket ? `Next bracket at ${formatWholeNumber(nextBracket)} payouts` : "No more brackets"}
      </ValueDescription>
      <ChartBox ref={ref} css={{ flex: "1 0 0px", overflow: "hidden" }}>
        {height !== undefined && (
          <Chart definition={definition} height={height} ariaLabel="Airwallex payouts by month" />
        )}
      </ChartBox>
    </Card>
  );
}
const feeStructure = [
  { count: 30000, fee: 0.03 },
  { count: 10000, fee: 0.08 },
  { count: 5000, fee: 0.2 },
  { count: 2000, fee: 0.35 },
];

function getAirwallexFeesUSD(transactionCount: number) {
  let total = 1000;
  let nextBracket = undefined;

  for (const { count, fee } of feeStructure) {
    if (transactionCount > count) {
      const remaining = transactionCount - count;
      total += remaining * fee;
      transactionCount = count;
    } else {
      nextBracket = count;
    }
  }

  return { total, nextBracket };
}

const BigValue = styled("p", {
  fontSize: 72,
  weight: 900,
  align: "center",
  lineHeight: 1,
});

const Brackets = styled("span", {
  fontSize: "0.5em",
  opacity: 0.5,
  weight: 400,
});

const ValueDescription = styled("p", {
  fontSize: 14,
  weight: 500,
  align: "center",
  lineHeight: 1,
  color: colors.text2,
});

type CardProps = Omit<BoxProps, "title"> & {
  title?: ReactNode;
};
function Card({ title, children, ...props }: CardProps) {
  return (
    <Box
      {...props}
      css={{
        pt: 16,
        rounded: 12,
        bg: "white",
        boxShadow: `inset 0 0 0 1px ${colors.outline2}`,
        ...props.css,
      }}
    >
      {title && (
        <Text
          css={{
            fontSize: 25,
            weight: 500,
            lineHeight: 1.25,
            align: "center",
            color: colors.navy,
            mb: 4,
          }}
        >
          {title}
        </Text>
      )}
      {children}
    </Box>
  );
}
